import { useIntl } from 'react-intl';

import Button from 'src/componentes/button';
import { Checkbox } from 'src/componentes/Form';
import LoadingContainer from 'src/componentes/loading-container';
import MapaCalor from 'src/componentes/mapa-calor';
import Spinner from 'src/componentes/spinner';
import useAuth from 'src/hooks/useAuth';
import { capitalizeFirstLetter } from 'src/utils/string';
import useDashboardMapaCalorNps from './useDashboardMapaCalorNps';

const DETRACTOR_PATTERN =
  'repeating-linear-gradient(135deg, transparent 0, transparent 4px, rgba(84, 27, 23, 0.12) 4px, rgba(84, 27, 23, 0.12) 6px)';

const RANGE_STYLES = {
  labelDetrator: {
    backgroundColor: '#e6a19b',
    backgroundImage: DETRACTOR_PATTERN,
    borderColor: '#d98780',
    color: '#541b17',
  },
  labelNeutro: {
    backgroundColor: '#f8c572',
    borderColor: '#e7b958',
    color: '#4a3200',
  },
  labelPromotor: {
    backgroundColor: '#9fce93',
    borderColor: '#83bd76',
    color: '#1f3b1c',
  },
};

const RANGE_IDS = ['labelDetrator', 'labelNeutro', 'labelPromotor'];

const styles = {
  heading: {
    marginBottom: 14,
    marginTop: 18,
  },
  title: {
    color: '#202124',
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 4,
  },
  subtitle: {
    color: '#7b7f87',
    fontSize: 13,
  },
  consolidationOption: {
    alignItems: 'center',
    color: '#344054',
    display: 'flex',
    fontSize: 13,
    gap: 10,
    marginTop: 12,
  },
  state: {
    alignItems: 'center',
    color: '#667085',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    justifyContent: 'center',
    minHeight: 180,
    padding: '32px 16px',
    textAlign: 'center',
  },
  loadingContainer: {
    minHeight: 180,
  },
  loadMore: {
    alignItems: 'center',
    display: 'flex',
    minHeight: 42,
  },
  loadMoreSpinner: {
    marginRight: 8,
  },
};

const toNullableNumber = (value) => {
  if (value == null || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getNpsClassification = (value) => {
  const number = Number(value);

  if (number <= 0) return 'labelDetrator';
  if (number <= 50) return 'labelNeutro';

  return 'labelPromotor';
};

const getScore = (result) =>
  toNullableNumber(
    result?.npsScore ??
      result?.npsScoreGeral ??
      result?.resultadoNps ??
      result?.mediaGeralNps
  );

const normalizeArea = (area, areaIndex) => {
  const cells = (area.resultados || []).reduce((result, current) => {
    if (current.dimensaoId == null) return result;

    result[String(current.dimensaoId)] = {
      validResponseCount: Number(
        current.totalRespostasValidas ?? current.totalRespostas ?? 0
      ),
      value: getScore(current),
    };

    return result;
  }, {});

  return {
    average: {
      validResponseCount: Number(
        area.totalRespostasValidas ?? area.totalRespostas ?? 0
      ),
      value: getScore(area),
    },
    cells,
    hasChildren: area.possuiFilhos === true,
    id: area.areaId ?? areaIndex,
    label: area.areaNome || '',
    level: Number(area.nivel || 0),
    parentId: area.areaPaiId,
  };
};

function NpsMapaCalor({ entidadeId, filtros, refreshToken }) {
  const intl = useIntl();
  const { terms: resource } = useAuth();
  const {
    changeConsolidation,
    changeRootPage,
    columns,
    consolidaAreasSubordinadas,
    erro,
    isLoading,
    loadMoreChildren,
    paginationCount,
    paginationCurrentPage,
    paginationPageSize,
    retryRoot,
    rows,
    toggleRow,
  } = useDashboardMapaCalorNps(entidadeId, {
    filtros,
    normalizeArea,
    refreshToken,
  });

  const ranges = RANGE_IDS.reduce((result, rangeId) => {
    result[rangeId] = {
      ...RANGE_STYLES[rangeId],
      statusLabel: intl.formatMessage({ id: rangeId }),
    };

    return result;
  }, {});

  const formatValue = (value) =>
    intl.formatNumber(Number(value), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

  const getCellAppearance = (value) => {
    const rangeId = getNpsClassification(value);
    return ranges[rangeId] || {};
  };

  const getValidResponsesLabel = (count) => {
    const resourceId =
      Number(count) === 1
        ? 'labelQuantidadeRespostaValida'
        : 'labelQuantidadeRespostasValidas';

    return intl.formatMessage({ id: resourceId }, { count });
  };

  const formatCellAriaLabel = ({
    appearance,
    cell,
    column,
    formattedValue,
    hasValue,
    row,
  }) => {
    if (!hasValue) {
      return intl.formatMessage(
        {
          id: 'labelMapaCalorCelulaSemDados',
        },
        {
          area: row.label,
          dimensao: column.label,
        }
      );
    }

    return intl.formatMessage(
      {
        id: 'labelMapaCalorCelulaDescricao',
      },
      {
        area: row.label,
        dimensao: column.label,
        faixa: appearance.statusLabel,
        respostas: getValidResponsesLabel(cell.validResponseCount || 0),
        valor: formattedValue,
      }
    );
  };

  const renderState = (message, action) => (
    <div style={styles.state}>
      <span>{message}</span>
      {action}
    </div>
  );

  return (
    <div>
      <div style={styles.heading}>
        <div style={styles.title}>
          {intl.formatMessage({
            id: 'labelMapaCalorNps',
          })}
        </div>
        <div style={styles.subtitle}>
          {intl.formatMessage({
            id: 'labelMapaCalorNpsDescricao',
          })}
        </div>
        <div style={styles.consolidationOption}>
          <Checkbox
            checked={consolidaAreasSubordinadas}
            id="switch-consolida-areas-subordinadas-mapa-calor-nps"
            label={intl.formatMessage(
              { id: 'consolidaAreasSubordinadas' },
              { areas: resource.areas }
            )}
            name="consolidaAreasSubordinadasMapaCalorNps"
            onChange={(event) =>
              changeConsolidation(event.target?.checked === true)
            }
            type="switch"
          />
        </div>
      </div>

      <LoadingContainer isLoading={isLoading} style={styles.loadingContainer}>
        {!isLoading &&
          erro &&
          renderState(
            intl.formatMessage({
              id: 'labelMapaCalorErroCarregamento',
            }),
            <Button color="secondary" onClick={retryRoot}>
              {intl.formatMessage({
                id: 'labelTentarNovamente',
              })}
            </Button>
          )}

        {!isLoading &&
          !erro &&
          (columns.length === 0 || rows.length === 0) &&
          renderState(
            intl.formatMessage({
              id: 'labelMapaCalorNpsSemDados',
            })
          )}

        {!isLoading && !erro && columns.length > 0 && rows.length > 0 && (
          <MapaCalor
            ariaLabel={intl.formatMessage({
              id: 'labelMapaCalorNps',
            })}
            averageLabel={intl.formatMessage({
              id: 'labelNpsMediaGeral',
            })}
            columns={columns}
            formatCellAriaLabel={formatCellAriaLabel}
            formatValue={formatValue}
            getCellAppearance={getCellAppearance}
            getCellTitle={({ cell, formattedValue, hasValue }) =>
              hasValue
                ? `${formattedValue} • ${getValidResponsesLabel(
                    cell.validResponseCount || 0
                  )}`
                : intl.formatMessage({
                    id: 'labelSemDados',
                  })
            }
            getRowToggleLabel={(row, isExpanded) =>
              `${intl.formatMessage({
                id: isExpanded ? 'colapsar' : 'expandir',
              })}: ${row.label}`
            }
            hierarchicalRows
            legend={RANGE_IDS.map((rangeId) => ({
              id: rangeId,
              label: ranges[rangeId].statusLabel,
              ...RANGE_STYLES[rangeId],
            }))}
            onChangePage={changeRootPage}
            onToggleRow={toggleRow}
            paginationCount={paginationCount}
            paginationCurrentPage={paginationCurrentPage}
            paginationPageSize={paginationPageSize}
            renderCustomRow={(row) => (
              <div
                style={{
                  ...styles.loadMore,
                  paddingLeft: `${row.level * 16 + 26}px`,
                }}
              >
                <Button
                  color="secondary"
                  disabled={row.isLoading}
                  onClick={() => loadMoreChildren(row)}
                >
                  {row.isLoading && (
                    <Spinner size={14} style={styles.loadMoreSpinner} />
                  )}
                  {intl.formatMessage({ id: 'labelCarregarMais' })}
                </Button>
              </div>
            )}
            rowHeaderLabel={capitalizeFirstLetter(resource.area)}
            rows={rows}
          />
        )}
      </LoadingContainer>
    </div>
  );
}

export default NpsMapaCalor;
